import request from '@src/util/request';
import HttpStatusCode from '@src/constants/HttpStatusCode';
import { Method } from 'axios';

const BASE = '/admin/voucher-reminder';

type ApiOk<T> = { code: number; message?: string; data: T; total?: number };

async function callApi<T>(
  url: string,
  options?: { method?: Method; data?: any; params?: any }
): Promise<{ ok: boolean; data?: T; total?: number; message?: string }> {
  const method = (options?.method || 'GET').toLowerCase() as Method;
  const qs =
    options?.params && method === 'get'
      ? `?${new URLSearchParams(
          Object.entries(options.params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => [k, String(v)])
        ).toString()}`
      : '';

  const res = await request<ApiOk<T>>({
    url: `${url}${qs}`,
    options: {
      method,
      data: options?.data,
    },
    noti: false,
  });

  if (!res || res.status !== HttpStatusCode.OK) {
    const msg =
      (res?.data as any)?.message ||
      (res?.data as any)?.issues?.[0]?.message ||
      'Request failed';
    return { ok: false, message: msg };
  }

  const body = res.data as any;
  if (body?.code !== 0 && body?.code !== undefined) {
    return { ok: false, message: body.message || 'error', data: body.data };
  }

  return { ok: true, data: body.data, total: body.total, message: body.message };
}

export const voucherReminderApi = {
  list: (
    params: {
      skip?: number;
      limit?: number;
      status?: string | null;
      name?: string | null;
      triggerType?: string | null;
      channel?: string | null;
      voucherId?: number | string | null;
    },
    pageCtx?: { pageId?: number | string; api?: string }
  ) =>
    callApi<any[]>(`${BASE}/list`, {
      method: 'GET',
      params: {
        ...params,
        ...(pageCtx?.pageId != null
          ? { page: pageCtx.pageId, api: pageCtx.api || 'list' }
          : {}),
      },
    }),

  detail: (id: number) =>
    callApi<any>(`${BASE}/detail`, { method: 'GET', params: { id } }),

  create: (data: any) =>
    callApi<any>(`${BASE}/create`, { method: 'POST', data }),

  update: (data: any) =>
    callApi<any>(`${BASE}/update`, { method: 'PATCH', data }),

  approve: (id: number) =>
    callApi<any>(`${BASE}/approve`, { method: 'POST', data: { id } }),

  reject: (id: number, reason?: string | null) =>
    callApi<any>(`${BASE}/reject`, {
      method: 'POST',
      data: { id, reason },
    }),

  setStatus: (id: number, status: 'active' | 'paused' | 'ended') =>
    callApi<any>(`${BASE}/set-status`, {
      method: 'POST',
      data: { id, status },
    }),

  preview: (data: {
    voucherIds: number[];
    triggerType: string;
    triggerValue: number;
    segment?: { tierIds?: number[]; provinceIds?: number[] };
  }) => callApi<{ total: number; sample: any[] }>(`${BASE}/preview`, {
    method: 'POST',
    data,
  }),

  report: (id: number) =>
    callApi<{ campaign: any; report: { total: number; byStatus: Record<string, number> } }>(
      `${BASE}/report`,
      { method: 'GET', params: { id } }
    ),

  templateVariables: (locale?: string) =>
    callApi<Array<{ key: string; token: string; description: string }>>(
      `${BASE}/template-variables`,
      {
        method: 'GET',
        params: locale ? { locale } : undefined,
      }
    ),
};

export default voucherReminderApi;
